const RoadTripPreparation = () => {
  return (
    <div className="bg-[#fff] w-full flex flex-col">
      <div
        className="relative w-full bg-cover bg-center h-[550px] flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/road-trip.png')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center px-6 md:px-12 lg:px-20 max-w-4xl">
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-4">
            How to Prepare Your Car for a Long Road Trip
          </h1>
          <p className="text-gray-200 text-lg md:text-xl">
            A safe and smooth road trip starts long before you hit the highway.
            Make sure your vehicle is ready for the journey ahead.
          </p>
        </div>
      </div>

      <section className="w-full px-4 py-10 md:px-12 lg:px-24 xl:px-32">
        <div className="mx-auto max-w-4xl text-gray-800 leading-relaxed">
          <p className="mb-6">
            Planning a long road trip involves more than packing bags and choosing a playlist —
            your vehicle must be fully prepared to handle long hours, varying road conditions, and changing weather.
            At <strong>Express Tire Works</strong>, we’ve created this comprehensive guide
            to ensure your car stays reliable, safe, and comfortable from start to finish.
          </p>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              1. Inspect Your Tires Thoroughly
            </h2>
            <p>
              Tires play a crucial role in stability, comfort, and fuel efficiency during long-distance travel.
              Before your trip, check:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Tread depth:</strong> Ensure your tires aren’t worn out.</li>
              <li><strong>Air pressure:</strong> Inflate according to manufacturer specifications.</li>
              <li><strong>Sidewall damage:</strong> Look for cracks, bulges, or cuts.</li>
              <li><strong>Spare tire:</strong> Confirm it is in good condition and properly inflated.</li>
            </ul>
            <p>
              If your tires are nearing the end of their lifespan, consider replacing them before your trip
              to prevent delays or breakdowns.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              2. Check Your Fluids
            </h2>
            <p>
              Long drives put additional stress on your vehicle’s engine and cooling systems.
              Make sure the following essential fluids are topped up:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Engine oil</li>
              <li>Coolant/antifreeze</li>
              <li>Brake fluid</li>
              <li>Transmission fluid</li>
              <li>Windshield washer fluid</li>
            </ul>
            <p>
              Fresh and clean fluids ensure your car runs efficiently, preventing overheating and mechanical failures.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              3. Test Your Battery and Electrical System
            </h2>
            <p>
              A weak battery can leave you stranded far from home.
              Visit a service center to check:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Battery charge level</li>
              <li>Terminal cleanliness and corrosion</li>
              <li>Alternator performance</li>
              <li>Proper functioning of lights and signals</li>
            </ul>
            <p>
              If your battery is more than 3 years old, consider replacing it as a precaution.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              4. Inspect Brakes and Suspension
            </h2>
            <p>
              You may encounter sharp turns, steep hills, or sudden traffic stops during a road trip.
              Ensure your braking and handling systems are in top shape.
            </p>

            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Brake pads and rotors</li>
              <li>Brake fluid levels</li>
              <li>Shock absorbers and struts</li>
              <li>Steering performance</li>
            </ul>

            <p>
              Smooth braking and stable handling are essential for long-distance safety.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              5. Ensure Your Tires Are Road Trip Ready
            </h2>
            <p>
              Many road trip issues come from neglected tires. If you haven't rotated your tires recently, do it before leaving. 
              Proper alignment also helps maintain stability and fuel efficiency during long drives.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              6. Prepare an Emergency Kit
            </h2>
            <p>Every long journey should include road trip essentials such as:</p>

            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>First-aid kit</li>
              <li>Jumper cables</li>
              <li>Portable tire inflator</li>
              <li>Flashlight with extra batteries</li>
              <li>Basic tools and gloves</li>
              <li>Water and non-perishable snacks</li>
            </ul>

            <p>
              Being prepared ensures peace of mind even in unexpected situations.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              7. Plan Your Route and Stops
            </h2>
            <p>
              Long-distance driving becomes easier when you plan ahead.
              Mark fuel stations, rest stops, scenic points, and food options on your route.
            </p>

            <p className="mt-3">
              Apps like Google Maps and Waze help track real-time traffic, construction zones, and alternative routes.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              8. Clean and Organize Your Vehicle
            </h2>
            <p>
              A clutter-free interior improves comfort during long drives.
              Clean windows, mirrors, and headlights enhance visibility — especially at night.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              Final Thoughts
            </h2>
            <p>
              A successful road trip depends on preparation. By ensuring your tires, brakes, fluids, and battery are ready,
              you can travel with confidence and enjoy every moment of your journey.
              At <strong>Express Tire Works</strong>, we’re here to help you get road trip–ready with expert tire checks,
              wheel services, and professional advice — before you hit the open road.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-3">
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Road Trip</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Car Maintenance</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Tire Safety</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Vehicle Prep</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Long Distance Travel</p>
        </div>
      </section>
    </div>
  );
};

export default RoadTripPreparation;